import React, { useEffect } from "react";
import { useRouter } from "next/router";

import PartnershipPage from "@/containers/PartnershipPage";
import YogaConditionPage from "@/containers/YogaConditionPage";
import HealthConditionPage from "@/containers/HealthConditionPage";
import PartnershipPrograms from "@/containers/PartnershipPrograms";
import NotFoundPage from "@/containers/NotFoundPage";

import {
  generateQueryArgumentsServerSide,
  generateUrlArguments,
  renderMetaTags,
} from "@/utils";
import { DEFAULT_URL_ARGUMENTS } from "@/constants";

//Redux
import { useAppDispatch, useAppSelector } from "@/hooks";
import * as healthProgramV1Actions from "@/redux/HealthProgramV1Reducer";
import * as yogaActions from "@/redux/YogaProgramReducer";
import * as healthActions from "@/redux/HealthProgramReducer";
import * as dashboardActions from "@/redux/DashboardReducer";
import * as corporateActions from "@/redux/PartnershipPageReducer";

import { wrapper } from "@/redux/Store";

import {
  yogaPages,
  healthPages,
  partnershipPages,
  corporatePartnerPages,
} from "@/constants/PagesList";

const getYogaProgramPage = (slug) => yogaActions.getYogaProgramPage({ slug });

const getHealthProgramPage = (slug) =>
  healthActions.getHealthProgramPage({ slug });

const getHealthLandingPageV1 = (slug) => {
  let _slug = slug;
  if (slug === "nutrition") _slug = "nutrition-homepage"; // Because "Nutrition" slug is already in use for Other API
  return healthProgramV1Actions.getHealthLandingPageV1({ slug: _slug });
};
const getCorporatePartnerShipPage = (slug: string) => {
  return corporateActions.getPartnershipPage({ slug });
};

const getPageAction = (slug: string) => {
  const _slug = slug?.toLowerCase();
  if (
    yogaPages.includes(_slug) ||
    _slug.endsWith("ambassador") ||
    _slug.startsWith("yoga-day")
  ) {
    return getYogaProgramPage(slug);
  } else if (healthPages.includes(_slug)) {
    return getHealthProgramPage(slug);
  } else if (partnershipPages.includes(_slug)) {
    return getHealthLandingPageV1(slug);
  } else if (
    corporatePartnerPages.includes(_slug) ||
    slug?.startsWith("corporate-partnerships")
  ) {
    return getCorporatePartnerShipPage(slug);
  } else {
    return () => {};
  }
};

const areSpecialPages = (slug: string) => {
  if (!slug) {
    return false;
  }
  slug = slug.toLowerCase();
  return (
    slug.startsWith("tata-health") ||
    slug.startsWith("redcliffe_health") ||
    slug.startsWith("tata-wellness") ||
    slug.startsWith("redcliffe_yoga") ||
    slug.startsWith("ketto-wellness")
  );
};

const getPageRedirectData = (query: any, slug: any) => {
  delete query.slug;
  const queryString = generateQueryArgumentsServerSide(query);
  let destination = "";
  if (slug.startsWith("tata-health") || slug.startsWith("redcliffe_health")) {
    destination = `/health-partnership/${slug}/${queryString}`;
  } else if (
    slug.startsWith("tata-wellness") ||
    slug.startsWith("redcliffe_yoga") ||
    slug.startsWith("ketto-wellness")
  ) {
    destination = `/wellness-partnership/${slug}/${queryString}`;
  } else {
    destination = `/${queryString}`;
  }
  return {
    redirect: {
      destination,
      permanent: true,
    },
  };
};

const TemplateManager = () => {
  const dispatch = useAppDispatch();

  const router = useRouter();
  const slug = router.query.slug as string;

  let urlParams = "";
  if (typeof window !== "undefined") {
    urlParams = window.location.search;
  }

  const { data } = useAppSelector((state) => state.healthProgramV1) || {};
  const { defaultAdsetName, page_type: template } = data?.forms || {};

  useEffect(() => {
    if (slug) dispatch(getPageAction(slug));
  }, [dispatch, slug]);

  const urlsArguments =
    generateUrlArguments(urlParams, {
      ...(defaultAdsetName && { adset_name: defaultAdsetName }),
      lead_via_partner_website: true,
    }) || DEFAULT_URL_ARGUMENTS;

  const renderTemplate = () => {
    const _slug = slug?.toLowerCase();
    if (
      yogaPages.includes(_slug) ||
      _slug.endsWith("ambassador") ||
      _slug.startsWith("yoga-day")
    ) {
      return <YogaConditionPage slug={slug} />;
    } else if (healthPages.includes(_slug)) {
      return <HealthConditionPage urlsArguments={urlsArguments} slug={slug} />;
    } else if (partnershipPages.includes(_slug)) {
      return <PartnershipPage slug={slug} />;
    } else if (
      corporatePartnerPages.includes(_slug) ||
      slug?.startsWith("corporate-partnerships")
    ) {
      return <PartnershipPrograms slug={slug} />;
    } else if (areSpecialPages(_slug)) {
      return <></>;
    } else {
      // return <HomePage urlParams={urlParams} slug={slug} />;
      return <NotFoundPage />;
    }
  };

  const component = renderTemplate();

  const metaTags = renderMetaTags(data);

  return (
    <>
      {metaTags}
      <main>{component}</main>
    </>
  );
};

export default TemplateManager;

const redirectionMap = (slug) => {
  switch (slug) {
    case "yoga":
      return "plans/yoga";
    case "yoga-for-pcos":
      return "plans/yoga/pcos";
    case "prenatal-yoga-and-breathwork":
      return "plans/yoga/prenatal-care";
    case "postnatal-yoga-and-breathwork":
      return "plans/yoga/postnatal-care";
    case "yoga-for-strength-and-flexibility":
      return "plans/yoga/strength-and-flexibility";
    case "yoga-for-wellness":
      return "plans/yoga/wellness";
    case "yoga-for-back-pain":
      return "plans/yoga/back-strengthening";
    // case "yoga-for-weight-management":
    //   return "plans/yoga/weight-loss";
    case "nutrition":
      return "plans/nutrition";
    case "nutrition-user-onboarding-flow":
      return "plans/nutrition/current-onboarding-status";
    default:
      return;
  }
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ query }) => {
      let q = { ...query };
      delete q.slug;
      let queryString = generateQueryArgumentsServerSide(q);
      const redirectURL = redirectionMap(query?.slug);
      if (redirectURL) {
        return {
          redirect: {
            destination: `/${redirectURL}/${queryString}`,
            permanent: true,
          },
        };
      }

      if (areSpecialPages(query.slug)) {
        return getPageRedirectData(query, query.slug);
      }
      await store.dispatch(getPageAction(query.slug));
      await store.dispatch(
        dashboardActions.getRibbonData({ pathname: query.slug })
      );
    }
);
